import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";

// Dark navy page hero used across inner pages
export const PageHero = ({ eyebrow, lines, intro, cta, accentIndex = 1 }) => (
  <section className="grain bg-[#16233f] text-white pt-40 pb-24 md:pt-48 md:pb-32">
    <div className="max-w-[1280px] mx-auto px-6 md:px-8">
      <Reveal>
        <div className="eyebrow mb-7">{eyebrow}</div>
      </Reveal>
      <Reveal delay={80}>
        <h1 className="font-serif font-semibold leading-[1.02] text-[42px] md:text-[72px] max-w-4xl">
          {lines.map((line, i) => (
            <span key={i} className="block">
              {i === accentIndex ? (
                <span className="italic text-[var(--gold)]">{line}</span>
              ) : (
                line
              )}
            </span>
          ))}
        </h1>
      </Reveal>
      {intro && (
        <Reveal delay={160}>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl mt-8">{intro}</p>
        </Reveal>
      )}
      {cta && (
        <Reveal delay={220}>
          <Link
            to={cta.path}
            className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-sm mt-10"
          >
            {cta.label} <ArrowRight size={18} />
          </Link>
        </Reveal>
      )}
    </div>
  </section>
);

// Numbered row (big ghost number + title + body) on light background
export const NumberedRow = ({ num, title, body, delay = 0, bordered = true }) => (
  <Reveal delay={delay}>
    <div className={`grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-9 ${bordered ? "border-t border-black/10" : ""}`}>
      <div className="font-serif text-[var(--gold)]/70 text-4xl md:text-5xl font-medium leading-none">
        {num}
      </div>
      <div>
        <h3 className="font-serif text-2xl md:text-[28px] font-semibold text-[#14161c] mb-3">
          {title}
        </h3>
        {body && <p className="text-[#55585f] leading-relaxed max-w-2xl">{body}</p>}
      </div>
    </div>
  </Reveal>
);

// Section header with sticky title on the left
export const SectionHead = ({ eyebrow, title, intro }) => (
  <div>
    <Reveal>
      <div className="eyebrow mb-5">{eyebrow}</div>
    </Reveal>
    <Reveal delay={80}>
      <h2 className="font-serif text-[38px] md:text-[52px] font-semibold leading-[1.05] text-[#14161c]">
        {title}
      </h2>
    </Reveal>
    {intro && (
      <Reveal delay={140}>
        <p className="text-[#55585f] leading-relaxed mt-6 max-w-md">{intro}</p>
      </Reveal>
    )}
  </div>
);

// Closing CTA band (navy)
export const CTABand = ({ title, body, button }) => (
  <section className="grain bg-[#1b2b4d] text-white py-24 md:py-32">
    <div className="max-w-[1280px] mx-auto px-6 md:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
      <div>
        <Reveal>
          <h2 className="font-serif text-[34px] md:text-[52px] font-semibold leading-[1.05] max-w-2xl">
            {title.split(" ").map((w, i, arr) =>
              i >= arr.length - 1 ? (
                <span key={i} className="italic text-[var(--gold)]">{w}</span>
              ) : (
                w + " "
              )
            )}
          </h2>
        </Reveal>
        {body && (
          <Reveal delay={100}>
            <p className="text-white/60 leading-relaxed max-w-xl mt-6">{body}</p>
          </Reveal>
        )}
      </div>
      <Reveal delay={140}>
        <Link
          to={button.path}
          className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-sm whitespace-nowrap"
        >
          {button.label} <ArrowRight size={18} />
        </Link>
      </Reveal>
    </div>
  </section>
);
